import { useCallback, useEffect, useState } from 'react';

import { SensorHistoricalEvent, SensorReadingBase } from '../../types';
import { useSensorEvents } from '../useSensorEvents';

const SECONDS_PER_HOUR = 60 * 60;

export const useGetSensorRollupByHours = <T extends SensorReadingBase>(
  sensorId: string | undefined,
  hours = 24,
  rollupInSeconds?: number,
): {
  historicalData: SensorHistoricalEvent<T>[] | null;
  loading: boolean;
  error: Error | null;
} => {
  const { fetchSensorHistoricalState, loading } = useSensorEvents(undefined, sensorId);
  const [historicalData, setHistoricalData] = useState<SensorHistoricalEvent<T>[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(
    async (sensorId: string, lookupHours: number, rollupInSeconds?: number) => {
      setError(null);

      const now = Math.floor(Date.now() / 1000);
      const startTime = now - lookupHours * SECONDS_PER_HOUR;
      const endTime = now;

      try {
        const data = await fetchSensorHistoricalState(
          sensorId,
          startTime,
          endTime,
          rollupInSeconds ?? SECONDS_PER_HOUR,
        );
        setHistoricalData(data as SensorHistoricalEvent<T>[]);
      } catch (error) {
        setError(error as Error);
        setHistoricalData(null);
      }
    },
    [fetchSensorHistoricalState],
  );

  useEffect(() => {
    if (!sensorId) {
      return;
    }
    fetchData(sensorId, hours, rollupInSeconds);
  }, [sensorId, hours, rollupInSeconds, fetchData]);

  return {
    historicalData,
    loading,
    error,
  };
};
